// markdown-it 15 ships its own types; the separate @types package is a
// different, older shape and the two do not agree.
import MarkdownIt from 'markdown-it';

import { API_PREFIX } from '@/lib/api/client';

/** Non-empty only when the API lives on its own host. */
const ATTACHMENT_ORIGIN = API_PREFIX.startsWith('http')
  ? API_PREFIX.replace(/\/api\/v1$/, '')
  : '';

/**
 * Markdown for task descriptions and comments.
 *
 * ## Why this is configured the way it is
 *
 * A delegated task's description is written by **someone else** and rendered in
 * your browser. That makes it untrusted input, so the renderer is locked down
 * rather than merely tidy:
 *
 * - `html: false` — raw HTML in the source is *escaped*, never emitted. This is
 *   what makes the output safe without a separate sanitiser: the only tags that
 *   ever reach the DOM are the fixed set markdown-it's own renderers produce.
 * - `validateLink` is replaced with a stricter allow-list than the default.
 *   markdown-it already blocks `javascript:` and friends; this narrows it
 *   further to the three schemes a task description has any business using.
 * - Link rendering adds `rel="noopener noreferrer"` and `target="_blank"`, so a
 *   link someone else wrote cannot reach back into this tab via `window.opener`.
 *
 * `linkify: true` turns a bare URL into a link, which is the other half of
 * "paste a link into a task and have it work".
 *
 * ## One trade-off worth naming
 *
 * Images from *external* hosts are allowed, because `![](https://…)` is what
 * people expect Markdown to do. The cost is that a description written by
 * someone else can load an image from a server they control, which tells that
 * server your IP and user agent — an ordinary tracking pixel. For a personal
 * tool shared with colleagues that is an acceptable price for the feature. If
 * this ever opens up to people you do not know, restrict image sources to our
 * own attachment endpoint and render everything else as a plain link.
 */

const ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:'];

/**
 * Our own attachment endpoint.
 *
 * Inline images are written as `![name](/api/v1/tasks/…/attachments/…/content)`,
 * which is a *relative* URL — and relative URLs are otherwise refused, because
 * a link that resolves against our own origin could point at an app route. This
 * pattern is narrow enough to be safe: it matches only the attachment content
 * path, with UUIDs in both positions.
 */
const ATTACHMENT_PATH =
  /^\/api\/v1\/(tasks|kb\/notes)\/[0-9a-f-]{36}\/attachments\/[0-9a-f-]{36}\/content$/i;

/**
 * ``[title](/tasks/{uuid})`` — a knowledge-base note referencing a task.
 * Allowed as a *relative* link because the click is intercepted and opens the
 * task detail in-app; it never navigates the document.
 */
const TASK_LINK_PATH = /^\/tasks\/[0-9a-f-]{36}$/i;

const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true, // a single newline is a line break — people write notes, not prose
  typographer: false,
});

/**
 * Only absolute web links and mail links.
 *
 * Anything else — `javascript:`, `data:`, `file:`, a relative path that could
 * be read as an app route — is refused, and markdown-it then renders the text
 * without a link rather than dropping it.
 */
md.validateLink = (url: string): boolean => {
  if (ATTACHMENT_PATH.test(url)) return true;
  if (TASK_LINK_PATH.test(url)) return true;

  try {
    // A base is required so a relative URL parses; a relative link resolving
    // against our own origin is exactly what we do not want to linkify.
    const parsed = new URL(url, 'https://invalid.example');
    if (parsed.origin === 'https://invalid.example' && !url.startsWith('mailto:')) {
      return false;
    }
    return ALLOWED_SCHEMES.includes(parsed.protocol);
  } catch {
    return false;
  }
};

// Links open in a new tab, and cannot reach back through window.opener.
const defaultLinkOpen =
  md.renderer.rules.link_open ??
  ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const href = String(tokens[idx]?.attrGet('href') ?? '');
  if (TASK_LINK_PATH.test(href)) {
    // Opens the task detail in-app — the click handler intercepts it.
    tokens[idx]?.attrSet('class', 'md-task-link');
    tokens[idx]?.attrSet('data-task-id', href.slice('/tasks/'.length));
    return defaultLinkOpen(tokens, idx, options, env, self);
  }
  tokens[idx]?.attrSet('target', '_blank');
  tokens[idx]?.attrSet('rel', 'noopener noreferrer');
  return defaultLinkOpen(tokens, idx, options, env, self);
};

/**
 * Inline images.
 *
 * Two things the default renderer does not do:
 *
 * - `loading="lazy"`, so a description with a dozen screenshots does not fetch
 *   all of them before the dialog paints;
 * - `crossorigin="use-credentials"` on our own attachment URLs when the API is
 *   a different origin — without it the browser sends no cookie and the image
 *   comes back 401.
 */
const defaultImage =
  md.renderer.rules.image ??
  ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));

md.renderer.rules.image = (tokens, idx, options, env, self) => {
  const token = tokens[idx];
  const source = String(token?.attrGet('src') ?? '');
  token?.attrSet('loading', 'lazy');
  if (ATTACHMENT_PATH.test(source) || source.startsWith(ATTACHMENT_ORIGIN)) {
    if (ATTACHMENT_ORIGIN) token?.attrSet('crossorigin', 'use-credentials');
  }
  return defaultImage(tokens, idx, options, env, self);
};

/**
 * Task lists — `- [ ] step` and `- [x] done`.
 *
 * Written as a renderer rule rather than pulled in as a plugin: the whole
 * behaviour is "swap the leading `[ ]` for a checkbox", and a dependency for
 * that would be more code to audit than the rule itself. It matters that the
 * checkbox is a real `<input>` and not a glyph — a description is often a
 * checklist, and ticking one off should be a click, not an edit.
 *
 * `disabled` unless the renderer is told otherwise: a description shown inside
 * a comment or a preview must not look actionable when nothing is listening.
 */
/**
 * Two spellings of the same thing. GitHub's `[ ]` / `[x]`, and Roam's
 * `{{TODO}}` / `{{DONE}}` — also as `{{[[TODO]]}}` / `{{[[DONE]]}}`, which is
 * what a Roam export actually contains. Notes pasted from Roam are common
 * enough that rendering the braces as text made the note look broken, and
 * asking people to rewrite every marker by hand is not an answer.
 */
const TASK_MARKER = /\[([ xX])\]|\{\{(\[\[)?(TODO|DONE)(\]\])?\}\}/;
const TASK_ITEM = new RegExp(`^(?:${TASK_MARKER.source})\\s*`);

/** Whether a marker — either spelling — reads as done. */
function markerChecked(marker: string): boolean {
  const m = TASK_MARKER.exec(marker);
  if (!m) return false;
  return m[1] !== undefined ? m[1] !== ' ' : m[3] === 'DONE';
}

/** The same marker, flipped, in the spelling it arrived in. */
function flipMarker(marker: string): string {
  const m = TASK_MARKER.exec(marker);
  if (!m) return marker;
  if (m[1] !== undefined) return m[1] === ' ' ? '[x]' : '[ ]';
  const open = m[2] ?? '';
  const close = m[4] ?? '';
  return `{{${open}${m[3] === 'DONE' ? 'TODO' : 'DONE'}${close}}}`;
}

/** The same marker, forced open, in the spelling it arrived in. */
function openMarker(marker: string): string {
  return markerChecked(marker) ? flipMarker(marker) : marker;
}

/** What `renderMarkdown` threads through markdown-it's `env`. */
interface RenderEnv {
  interactiveTasks?: boolean;
  taskItemPending?: boolean;
  taskItemIndex?: number;
  /** lowercased title → note id (null = phantom). Present only for KB notes. */
  kbLinks?: Map<string, string | null>;
  // markdown-it types `env` as an open record keyed by string *or symbol*, so
  // the shape has to stay open on both.
  [key: string | symbol]: unknown;
}

const defaultInline =
  md.renderer.rules.text ??
  ((tokens, idx) => md.utils.escapeHtml(String(tokens[idx]?.content ?? '')));

md.renderer.rules.text = (tokens, idx, options, rawEnv, self) => {
  const env = rawEnv as RenderEnv;
  const token = tokens[idx];
  const match = TASK_ITEM.exec(String(token?.content ?? ''));
  // Only the *first* text token of a list item can open a task item; anywhere
  // else `[ ]` is just brackets.
  if (!match || env?.taskItemPending !== true) {
    return defaultInline(tokens, idx, options, env, self);
  }
  env.taskItemPending = false;

  const checked = markerChecked(match[0]);
  const rest = String(token?.content ?? '').slice(match[0].length);
  const index = env.taskItemIndex ?? 0;
  env.taskItemIndex = index + 1;

  return (
    `<input type="checkbox" class="md-task" data-task-index="${index}"` +
    `${checked ? ' checked' : ''}${env.interactiveTasks ? '' : ' disabled'}> ` +
    md.utils.escapeHtml(rest)
  );
};

// A list item is where a task item may begin.
const defaultParagraphOpen =
  md.renderer.rules.paragraph_open ??
  ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));

md.renderer.rules.paragraph_open = (tokens, idx, options, rawEnv, self) => {
  const env = rawEnv as RenderEnv | undefined;
  if (env) env.taskItemPending = true;
  return defaultParagraphOpen(tokens, idx, options, rawEnv, self);
};

/**
 * ``[[Wikilinks]]`` for knowledge-base notes.
 *
 * An inline rule rather than a preprocessing regex: markdown-it already knows
 * where code spans and fences are, so a rule registered in the inline chain
 * gets "not inside code" for free — the exact property the backend parser has
 * to re-implement by hand.
 *
 * The rule only fires when the render env carries a ``kbLinks`` map (from the
 * note's server-side link rows), so task descriptions and comments are
 * completely unaffected. Resolved links navigate; phantoms render dashed and
 * carry the title, so a click can offer to create the note.
 */
md.inline.ruler.before('link', 'kb_wikilink', (state, silent) => {
  const env = state.env as RenderEnv;
  if (!env?.kbLinks) return false;
  const source = state.src;
  if (
    source.charCodeAt(state.pos) !== 0x5b ||
    source.charCodeAt(state.pos + 1) !== 0x5b
  ) {
    return false;
  }
  const close = source.indexOf(']]', state.pos + 2);
  if (close < 0) return false;
  const title = source.slice(state.pos + 2, close).trim();
  if (!title || title.includes('[') || title.includes('\n')) return false;

  if (!silent) {
    const target = env.kbLinks.get(title.toLowerCase());
    const token = state.push('kb_wikilink', '', 0);
    token.content = title;
    token.meta = { noteId: target ?? null };
  }
  state.pos = close + 2;
  return true;
});

md.renderer.rules.kb_wikilink = (tokens, idx) => {
  const token = tokens[idx];
  const title = md.utils.escapeHtml(String(token?.content ?? ''));
  const noteId = token?.meta?.noteId as string | null;
  if (noteId) {
    return `<a class="kb-wikilink" data-note-id="${md.utils.escapeHtml(noteId)}" href="#">${title}</a>`;
  }
  return `<a class="kb-wikilink kb-phantom" data-create-title="${title}" href="#">${title}</a>`;
};

/** Render Markdown to HTML. The result is safe to inject. */
export function renderMarkdown(
  source: string,
  {
    interactive = false,
    kbLinks,
  }: { interactive?: boolean; kbLinks?: Map<string, string | null> } = {},
): string {
  // Roam's bracketed spelling, `{{[[TODO]]}}`, contains a `[[…]]` that the
  // wikilink rule would otherwise claim before the task rule ever sees it.
  // Rendering normalises the marker to `{{TODO}}` line by line; the stored text
  // keeps its spelling, and the rewriters flip it in place.
  const normalised = rewriteTaskLines(source, (line, match) =>
    `${match[1]}${(match[2] ?? '').replace('{{[[', '{{').replace(']]}}', '}}')}${line.slice(match[0].length)}`,
  );
  return md.render(normalised, {
    interactiveTasks: interactive,
    taskItemIndex: 0,
    kbLinks,
  });
}

/** A checklist line: list marker, then a task marker in either spelling. */
const TASK_LINE = new RegExp(`^(\\s*(?:[-*+]|\\d+[.)])\\s+)(${TASK_MARKER.source})`);

/**
 * Walk the source line by line, replacing checklist lines.
 *
 * Fenced code is skipped: the renderer never numbers a `- [ ]` inside a code
 * block, so a rewriter that counted it would flip the wrong box on every
 * checklist below the fence.
 */
function rewriteTaskLines(
  source: string,
  rewrite: (line: string, match: RegExpExecArray, ordinal: number) => string,
): string {
  let seen = -1;
  let fenced = false;
  return source
    .split('\n')
    .map((line) => {
      if (/^\s*(```|~~~)/.test(line)) {
        fenced = !fenced;
        return line;
      }
      if (fenced) return line;
      const match = TASK_LINE.exec(line);
      if (!match) return line;
      seen += 1;
      return rewrite(line, match, seen);
    })
    .join('\n');
}

/**
 * Flip the nth `- [ ]` in the source.
 *
 * Rewrites the *Markdown*, not the rendered HTML — the description is the
 * stored value, so ticking a box has to change the text that will be saved.
 * Counted in document order, which is the same order the renderer numbered
 * them in.
 */
export function toggleTaskItem(source: string, index: number): string {
  return rewriteTaskLines(source, (line, match, ordinal) => {
    if (ordinal !== index) return line;
    return `${match[1]}${flipMarker(match[2] ?? '')}${line.slice(match[0].length)}`;
  });
}

/** How a checklist is doing — for the "3/7" beside the add-item field. */
export function taskItemStats(source: string): { done: number; total: number } {
  let done = 0;
  let total = 0;
  rewriteTaskLines(source, (line, match) => {
    total += 1;
    if (markerChecked(match[2] ?? '')) done += 1;
    return line;
  });
  return { done, total };
}

/**
 * Add items to the checklist — after its last line, so a list that sits above
 * prose grows in place instead of sprouting a second list at the bottom.
 * No checklist yet means starting one at the end.
 */
export function appendTaskItems(source: string, items: string[]): string {
  const cleaned = items.map((item) => item.trim()).filter(Boolean);
  if (cleaned.length === 0) return source;
  const addition = cleaned.map((item) => `- [ ] ${item}`).join('\n');

  const lines = source.split('\n');
  let last = -1;
  let fenced = false;
  lines.forEach((line, index) => {
    if (/^\s*(```|~~~)/.test(line)) fenced = !fenced;
    else if (!fenced && TASK_LINE.test(line)) last = index;
  });

  if (last >= 0) {
    lines.splice(last + 1, 0, addition);
    return lines.join('\n');
  }
  const body = source.trimEnd();
  return body ? `${body}\n\n${addition}` : addition;
}

/** Every box back to `[ ]` — the weekly shop is the same list, restarted. */
export function uncheckAllTaskItems(source: string): string {
  return rewriteTaskLines(
    source,
    (line, match) => `${match[1]}${openMarker(match[2] ?? '')}${line.slice(match[0].length)}`,
  );
}

/** Render a single line — for comments and previews, where blocks would be odd. */
export function renderMarkdownInline(source: string): string {
  return md.renderInline(source);
}
